import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatures } from '../../contexts/FeaturesContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import { queryKeys } from '../../hooks/queries/queryKeys';
import { fetchHelmUnreadCounts } from '../../hooks/queries/helmUnreadCounts';
import type { DropdownState } from './types';

// Spec 031 backoff constants -- unchanged values from the pre-migration
// usePollingWithBackoff() call, now driving useQuery's refetchInterval
// directly instead of a hand-rolled setTimeout loop (Spec 294 category 3).
const INITIAL_INTERVAL = 30000; // 30 seconds
const MAX_INTERVAL = 300000; // 5 minutes
const BACKOFF_MULTIPLIER = 2;

/**
 * Pure backoff step, extracted so it's unit-testable without driving real
 * (or fake) timers through React/TanStack. Mirrors the deleted
 * usePollingWithBackoff()'s signalIdle()/signalActive() exactly:
 *   - a failed fetch leaves the interval unchanged ("retry at same rate")
 *   - a successful fetch with zero unread items backs off (x2, capped)
 *   - a successful fetch with any unread items resets to the initial interval
 * Exported for the corresponding unit test in __tests__/useLayout.test.ts.
 */
export function computeNextHelmUnreadPollInterval(
  currentInterval: number,
  queryState: { status: 'pending' | 'error' | 'success'; data?: number }
): number {
  if (queryState.status === 'error') {
    return currentInterval;
  }
  const total = queryState.data ?? 0;
  return total === 0
    ? Math.min(currentInterval * BACKOFF_MULTIPLIER, MAX_INTERVAL)
    : INITIAL_INTERVAL;
}

export function useLayout() {
  const { user, logout } = useAuth();
  const { features } = useFeatures();
  const { organization: selectedOrg } = useOrganization();
  const navigate = useNavigate();
  const location = useLocation();

  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobilePersonalExpanded, setMobilePersonalExpanded] = useState(false);
  const [mobileProgressExpanded, setMobileProgressExpanded] = useState(false);
  const [mobileHierarchyExpanded, setMobileHierarchyExpanded] = useState(false);
  const [mobileAccountExpanded, setMobileAccountExpanded] = useState(false);
  const [mobileProtocolExpanded, setMobileProtocolExpanded] = useState(false);
  const [mobileCommunityExpanded, setMobileCommunityExpanded] = useState(false);

  // Desktop dropdown state
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [orgDropdownOpen, setOrgDropdownOpen] = useState(false);
  const [adminDropdownOpen, setAdminDropdownOpen] = useState(false);
  const [progressDropdownOpen, setProgressDropdownOpen] = useState(false);
  const [protocolDropdownOpen, setProtocolDropdownOpen] = useState(false);
  const [personalDropdownOpen, setPersonalDropdownOpen] = useState(false);
  const [communityDropdownOpen, setCommunityDropdownOpen] = useState(false);

  // Praise modal
  const [praiseModalOpen, setPraiseModalOpen] = useState(false);

  // Dropdown refs
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const orgDropdownRef = useRef<HTMLDivElement>(null);
  const adminDropdownRef = useRef<HTMLDivElement>(null);
  const progressDropdownRef = useRef<HTMLDivElement>(null);
  const protocolDropdownRef = useRef<HTMLDivElement>(null);
  const personalDropdownRef = useRef<HTMLDivElement>(null);
  const communityDropdownRef = useRef<HTMLDivElement>(null);

  // Close mobile menu and dropdowns when route changes
  useEffect(() => {
    setMobileMenuOpen(false);
    setMobilePersonalExpanded(false);
    setMobileProgressExpanded(false);
    setMobileProtocolExpanded(false);
    setMobileHierarchyExpanded(false);
    setMobileAccountExpanded(false);
    setMobileCommunityExpanded(false);
    setUserDropdownOpen(false);
    setOrgDropdownOpen(false);
    setAdminDropdownOpen(false);
    setProgressDropdownOpen(false);
    setProtocolDropdownOpen(false);
    setPersonalDropdownOpen(false);
    setCommunityDropdownOpen(false);
  }, [location.pathname]);

  // Organization is now provided by OrganizationContext (reactive to changes)

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setUserDropdownOpen(false);
      }
      if (orgDropdownRef.current && !orgDropdownRef.current.contains(event.target as Node)) {
        setOrgDropdownOpen(false);
      }
      if (adminDropdownRef.current && !adminDropdownRef.current.contains(event.target as Node)) {
        setAdminDropdownOpen(false);
      }
      if (progressDropdownRef.current && !progressDropdownRef.current.contains(event.target as Node)) {
        setProgressDropdownOpen(false);
      }
      if (protocolDropdownRef.current && !protocolDropdownRef.current.contains(event.target as Node)) {
        setProtocolDropdownOpen(false);
      }
      if (personalDropdownRef.current && !personalDropdownRef.current.contains(event.target as Node)) {
        setPersonalDropdownOpen(false);
      }
      if (communityDropdownRef.current && !communityDropdownRef.current.contains(event.target as Node)) {
        setCommunityDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Spec 031 + Spec 294: unified Helm unread count for the header badge.
  // currentIntervalRef holds the progressive-backoff state across fetches
  // (30s -> 60s -> ... -> 5min cap on consecutive zero-unread results,
  // reset to 30s on any nonzero result) -- the same state
  // usePollingWithBackoff used to own, now driving useQuery's
  // refetchInterval directly.
  //
  // Shares its query key AND its queryFn with useHelmReadStatus.ts (same
  // endpoint, same orgId param, same `queries/helmUnreadCounts.ts` fetch) --
  // this used to be a bare-number queryFn while useHelmReadStatus.ts kept
  // the full UnreadCounts object under the same key, which is a TanStack
  // Query key/shape collision (only one queryFn can win for a given key).
  // Fixed 2026-08-03: both hooks now fetch and cache the identical
  // UnreadCounts shape; this hook just reads `.total` off of it for the
  // header badge. See helmUnreadCounts.ts's docstring for the full story.
  const currentIntervalRef = useRef(INITIAL_INTERVAL);

  const helmUnreadQuery = useQuery({
    queryKey: queryKeys.helm.unreadCount(selectedOrg?.id ? String(selectedOrg.id) : '', user?.id || ''),
    queryFn: ({ signal }) => fetchHelmUnreadCounts(String(selectedOrg!.id), signal),
    enabled: !!user && !!selectedOrg?.id,
    // Pre-migration fetchHelmUnreadCount() had no retry wrapper -- errors
    // were swallowed silently and the badge just kept its last value.
    retry: false,
    refetchInterval: (query) => {
      currentIntervalRef.current = computeNextHelmUnreadPollInterval(currentIntervalRef.current, {
        status: query.state.status,
        data: query.state.data?.total,
      });
      return currentIntervalRef.current;
    },
  });

  const helmUnreadCount = helmUnreadQuery.data?.total ?? 0;

  // Refresh immediately when navigating (pre-migration: helmPolling.fetchNow())
  useEffect(() => {
    if (user && selectedOrg?.id) {
      helmUnreadQuery.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path: string) => {
    return location.pathname === path ? 'active' : '';
  };

  // Helper to open one dropdown and close all others
  const openDropdown = (dropdown: keyof DropdownState) => {
    setUserDropdownOpen(dropdown === 'userDropdownOpen' ? !userDropdownOpen : false);
    setOrgDropdownOpen(dropdown === 'orgDropdownOpen' ? !orgDropdownOpen : false);
    setAdminDropdownOpen(dropdown === 'adminDropdownOpen' ? !adminDropdownOpen : false);
    setProgressDropdownOpen(dropdown === 'progressDropdownOpen' ? !progressDropdownOpen : false);
    setProtocolDropdownOpen(dropdown === 'protocolDropdownOpen' ? !protocolDropdownOpen : false);
    setPersonalDropdownOpen(dropdown === 'personalDropdownOpen' ? !personalDropdownOpen : false);
    setCommunityDropdownOpen(dropdown === 'communityDropdownOpen' ? !communityDropdownOpen : false);
  };

  return {
    user,
    features,
    location,
    helmUnreadCount,
    selectedOrg,
    praiseModalOpen,
    setPraiseModalOpen,
    dropdownState: {
      userDropdownOpen,
      orgDropdownOpen,
      adminDropdownOpen,
      progressDropdownOpen,
      protocolDropdownOpen,
      personalDropdownOpen,
      communityDropdownOpen,
    },
    setUserDropdownOpen,
    setOrgDropdownOpen,
    setAdminDropdownOpen,
    setProgressDropdownOpen,
    setProtocolDropdownOpen,
    setPersonalDropdownOpen,
    setCommunityDropdownOpen,
    dropdownRefs: {
      userDropdownRef,
      orgDropdownRef,
      adminDropdownRef,
      progressDropdownRef,
      protocolDropdownRef,
      personalDropdownRef,
      communityDropdownRef,
    },
    mobileMenuState: {
      mobileMenuOpen,
      mobilePersonalExpanded,
      mobileProgressExpanded,
      mobileHierarchyExpanded,
      mobileAccountExpanded,
      mobileProtocolExpanded,
      mobileCommunityExpanded,
    },
    setMobileMenuOpen,
    setMobilePersonalExpanded,
    setMobileProgressExpanded,
    setMobileHierarchyExpanded,
    setMobileAccountExpanded,
    setMobileProtocolExpanded,
    setMobileCommunityExpanded,
    handleLogout,
    isActive,
    openDropdown,
  };
}
